import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { AIProcessingBanner, CurrencyPicker, DialogForm, UserPicker } from '../../../shared/components/common';
import { CompanyCreateField, useCompanyCreatePrecedence } from '../../../shared/components/multi-company';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import type { PendingLineItem } from './InvoiceLineEditor';
import { OrganizationPicker } from '../../master_data/contacts/components/OrganizationPicker';
import { ContactPicker } from '../../master_data/contacts/components/ContactPicker';
import { useAccountingOperationalDefaults } from '../hooks/useSettings';
import { useTenantLocaleSettings } from '../../../shared/hooks/useTenantSettings';
import { useRevenueCategoryRules, useCreateRevenueCategoryRule } from '../hooks/useRevenueCategoryRules';
import { aiApi } from '../services/accountingApi';
import type { InvoiceCreate, AccountSuggestion } from '../types';
import { toDateOnlyString } from '@/shared/utils/dateFormat';

interface InvoiceQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: InvoiceCreate) => void;
  isLoading?: boolean;
  prefill?: {
    customer_name?: string;
    issue_date?: string;
    due_date?: string;
    reference?: string;
    notes?: string;
    lines?: PendingLineItem[];
  };
}

export function InvoiceQuickCreateDialog({ open, onClose, onSubmit, isLoading, prefill }: InvoiceQuickCreateDialogProps) {
  const { t } = useTranslation('accounting');
  // Member-readable: the tenant default sales account seeds the invoice-level
  // revenue category, so an admin-gated read would leave a plain member's new
  // invoice unseeded rather than refused.
  const { data: operationalDefaults } = useAccountingOperationalDefaults();
  const { data: localeSettings } = useTenantLocaleSettings();
  const { data: rulesData } = useRevenueCategoryRules();
  const createRule = useCreateRevenueCategoryRule();

  const companyPrecedence = useCompanyCreatePrecedence();
  const { defaultCompanyId } = companyPrecedence;
  const [companyId, setCompanyId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [contactId, setContactId] = useState('');
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [issueDate, setIssueDate] = useState(toDateOnlyString(new Date()));
  const [dueDate, setDueDate] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [parsedLines, setParsedLines] = useState<PendingLineItem[]>([]);
  const [invoiceLevelAccountId, setInvoiceLevelAccountId] = useState('');
  const [currencyCode, setCurrencyCode] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<(AccountSuggestion & { organizationId: string }) | null>(null);
  // A revenue-category suggestion is in flight. Bumping the id supersedes any
  // outstanding request so a fast customer re-pick can't be overtaken by the
  // previous customer's answer.
  const [aiPending, setAiPending] = useState(false);
  const aiRequestId = useRef(0);


  useEffect(() => {
    if (open) {
      // Company precedence is applied by the fill effect below, not here.
      setCompanyId('');
      setCustomerId('');
      setCustomerName(prefill?.customer_name || '');
      setContactId('');
      setEmployeeId(null);
      setCurrencyCode(localeSettings?.default_currency || null);
      setAiSuggestion(null);
      setInvoiceLevelAccountId(operationalDefaults?.default_sales_account_id || '');
      const today = toDateOnlyString(new Date());
      setIssueDate(prefill?.issue_date || today);
      const due = new Date();
      due.setDate(due.getDate() + 30);
      setDueDate(prefill?.due_date || toDateOnlyString(due));
      setReference(prefill?.reference || '');
      setNotes(prefill?.notes || '');
      setParsedLines(prefill?.lines || []);
    }
  }, [open, prefill]);

  // `defaultCompanyId` needs the accessible-company list, so it resolves AFTER
  // the first render. It must NOT sit in the reset effect above — that effect
  // rebuilds the WHOLE form, so a late resolution would wipe everything typed
  // since the dialog opened. Fill the still-blank company field on its own
  // instead; a user pick is never overwritten. Stays empty under an "All
  // companies" scope, where the required picker blocks submit.
  useEffect(() => {
    if (open && !companyId && defaultCompanyId) setCompanyId(defaultCompanyId);
  }, [open, companyId, defaultCompanyId]);

  const revenueCategoryRules = rulesData?.data || [];

  const handleSubmit = () => {
    if (!dueDate) return;
    // Multi-company guard: when scope is "All companies" the required picker
    // stays empty and we block submit until the user picks. In a specific
    // scope the chip pre-fills companyId (one-click happy path); single-company
    // tenants render nothing and the backend defaults to the only company.
    if (companyPrecedence.blockCreate(companyId)) return;

    // Include any parsed lines from document upload
    const defaultAccountId = invoiceLevelAccountId || operationalDefaults?.default_sales_account_id || '';
    const resolvedLines = parsedLines
      .filter((l) => l.description && l.unit_price > 0)
      .map((l) => ({
        description: l.description,
        account_id: l.account_id || defaultAccountId,
        offering_id: l.offering_id || undefined,
        quantity: l.quantity,
        unit_price: l.unit_price,
        discount_percent: l.discount_percent,
        tax_rate_id: l.tax_rate_id || undefined,
        tax_amount: l.tax_amount,
      }));

    onSubmit({
      company_id: companyId || undefined,
      customer_id: customerId || undefined,
      customer_name: customerName || undefined,
      contact_id: contactId || undefined,
      employee_id: employeeId || undefined,
      currency_code: currencyCode || undefined,
      issue_date: issueDate,
      due_date: dueDate,
      reference: reference || undefined,
      notes: notes || undefined,
      lines: resolvedLines.length > 0 ? resolvedLines : undefined,
    });
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={t('invoices.createInvoice')}
      submitLabel={t('invoices.createInvoice')}
      isLoading={isLoading}
      maxWidth="lg"
    >
      <div className="space-y-4">
        <CompanyCreateField
          precedence={companyPrecedence}
          value={companyId}
          onChange={setCompanyId}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('invoices.customer')}</Label>
            <OrganizationPicker
              value={customerId}
              onChange={(id) => {
                setCustomerId(id);
                setContactId('');
              }}
              onCompanySelected={(company) => {
                setCustomerName(company?.name ?? '');
                setAiSuggestion(null);
                aiRequestId.current += 1;
                setAiPending(false);
                // Auto-fill currency from customer's default (user can override)
                if (company?.default_currency_code) {
                  setCurrencyCode(company.default_currency_code);
                } else {
                  setCurrencyCode(localeSettings?.default_currency || null);
                }
                // Check for customer revenue category rule first
                const customerRule = company ? revenueCategoryRules.find(
                  (r) => r.entity_type === 'customer' && r.entity_id === company.id
                ) : undefined;
                if (customerRule) {
                  setInvoiceLevelAccountId(customerRule.account_id);
                } else if (company) {
                  // AI suggestion as last resort. It used to arrive with no
                  // warning that it was coming and then overwrite whatever the
                  // user had picked in the meantime — easy to outrun, and its
                  // answer won. Announce the wait, and stand down if the user
                  // moved on (picked another customer, or chose an account).
                  const requestId = ++aiRequestId.current;
                  setAiPending(true);
                  aiApi.suggestAccount(company.name, 'revenue', company.industry ?? undefined).then((res) => {
                    if (requestId !== aiRequestId.current) return; // superseded
                    const suggestion = res.data;
                    const suggestedAccountId = suggestion.account_id;
                    if (suggestedAccountId) {
                      setAiSuggestion({ ...suggestion, organizationId: company.id });
                      // Never clobber an account the user chose while waiting.
                      setInvoiceLevelAccountId((current) => current || suggestedAccountId);
                    }
                  }).catch(() => { /* Silently fail — user can pick manually */ })
                    .finally(() => {
                      if (requestId === aiRequestId.current) setAiPending(false);
                    });
                }
              }}
              organizationRelationshipFilter="customer"
              placeholder={t('invoices.customerPlaceholder')}
              addNewLabel={t('contacts:companyPicker.addNewCustomer')}
              defaultRelationships={['customer']}
              allowClear
            />
          </div>
          <div className="space-y-2">
            <ContactPicker
              value={contactId}
              onChange={setContactId}
              organizationId={customerId || undefined}
              label={t('invoices.contact')}
              placeholder={t('invoices.contactPlaceholder')}
              disabled={!customerId}
              defaultCompanyId={customerId || undefined}
              allowClear
            />
          </div>
        </div>

        {aiPending && !aiSuggestion && (
          <AIProcessingBanner message={t('common:aiProcessing.analyzing')} />
        )}

        {aiSuggestion && (
          <div className="flex items-center gap-2 p-2 bg-primary/5 rounded text-sm">
            <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="flex-1">{t('invoices.aiSuggestedAccount', { account: aiSuggestion.account_name })}</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => {
                if (aiSuggestion.account_id) createRule.mutate({ account_id: aiSuggestion.account_id, entity_type: 'customer', entity_id: aiSuggestion.organizationId });
                setAiSuggestion(null);
              }}
            >
              {t('common:app.confirm')}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAiSuggestion(null)}>
              {t('common:app.dismiss')}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('invoices.currency')}</Label>
            <CurrencyPicker
              value={currencyCode}
              onChange={setCurrencyCode}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('invoices.reference')}</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t('invoices.referencePlaceholder')}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('invoices.employee')}</Label>
          <UserPicker
            value={employeeId}
            onChange={setEmployeeId}
            placeholder={t('invoices.employeePlaceholder')}
            filterUserTypes={['internal']}
            size="sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('invoices.issueDate')}</Label>
            <Input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('invoices.dueDate')}</Label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('invoices.notes')}</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('invoices.notesPlaceholder')}
            rows={2}
          />
        </div>
      </div>
    </DialogForm>
  );
}
